import React, { useEffect, useState } from "react";
import Table from "../shared/table/Table";
import { FiEdit3, FiSliders, FiEye } from "react-icons/fi";
import { useNavigate, useLocation } from "react-router-dom";
import { fetchData, formatDateOnly, fetchFormData } from "../../utils/options";
import { MdThumbDown, MdThumbUp } from "react-icons/md";
import FollowUpModal from "../../utils/FollowUpModal";
import InquiryStageModal from "../../utils/InquiryStageModal";
import SettledModal from "../../utils/SettledModal";
import { FaMoneyBillTrendUp } from "react-icons/fa6";
import Swal from "sweetalert2";
import topTost from "../../utils/topTost";

const OpenLoanLeadsTable = ({ filteredData = null, permission }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const M_id = queryParams.get("id");
  const userId = localStorage.getItem("id");
  const [branchData, setBranchData] = useState([]);
  const [successMessage, setSuccessMessage] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const [showSettledModal, setShowSettledModal] = useState(false);
  const [isSettled, setIsSettled] = useState(false);
  const [selectedInquiryId, setSelectedInquiryId] = useState(null);
  const [payoutAmount, setPayoutAmount] = useState("");
  const [payoutDate, setPayoutDate] = useState("");
  const [payoutType, setPayoutType] = useState("");
  const [remark, setremark] = useState("");
  const [transactionDetails, setTransactionDetails] = useState("");
  const [selectedRowIds, setSelectedRowIds] = useState([]);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (localStorage.getItem("access_token") === null) {
      navigate("/");
    }
  }, []);

  const handleSubmit = () => {
    setShowSettledModal(true); // Open the popup
  };

  const fetchInitialData = async () => {
    const userId = localStorage.getItem("id");
    const formData = new FormData();
    formData.append("userId", userId);
    formData.append("moduleId", M_id);

    try {
      Swal.fire({
        title: "Loading...",
        allowOutsideClick: false,
        didOpen: () => {
          Swal.showLoading();
        },
      });

      const [branchResult] = await Promise.all([
        fetchFormData("list-converted-inquiries", formData, "POST"),
      ]);

      if (branchResult) {
        setBranchData(branchResult.users || []);
      }
    } catch (error) {
      console.error("Error fetching initial data:", error);
      Swal.fire("Error", "Failed to fetch loan lead data", "error");
    } finally {
      Swal.close(); // Hide loader
    }
  };

  useEffect(() => {
    fetchInitialData();
  }, []);

  const columns = [
    {
      id: "select",
      header: () => (
        <input
          type="checkbox"
          onChange={() => {
            if (selectedRowIds.length === branchData.length) {
              setSelectedRowIds([]);
            } else {
              setSelectedRowIds(branchData.map((row) => row.id));
            }
          }}
          checked={selectedRowIds.length === branchData.length}
          ref={(input) => {
            if (input) {
              input.indeterminate =
                selectedRowIds.length > 0 &&
                selectedRowIds.length < branchData.length;
            }
          }}
        />
      ),
      cell: ({ row }) => {
        const id = row.original.id;
        return (
          <input
            type="checkbox"
            checked={selectedRowIds.includes(id)}
            onChange={() => {
              setSelectedRowIds((prev) =>
                prev.includes(id)
                  ? prev.filter((item) => item !== id)
                  : [...prev, id]
              );
              setSelectedInquiryId(id);
            }}
          />
        );
      },

      meta: {
        headerClassName: "width-10",
        exportable: false,
      },
    },
    ...(permission?.pf_edit === 1 || permission?.pf_status === 1
      ? [
          {
            accessorKey: "actions",
            header: () => "Actions",
            cell: ({ row }) => {
              const id = row.original.id;

              return (
                <div className="hstack gap-2 justify-content-end">
                  {permission?.pf_edit === 1 && (
                    <span
                      className="cursor-pointer"
                      onClick={() =>
                        navigate(`/inquiry-converted/history/${id}`)
                      }
                      title="Converted History"
                    >
                      <FiEye size={16} />
                    </span>
                  )}
                  {permission?.pf_edit === 1 && (
                    <span
                      className="cursor-pointer"
                      onClick={() => {
                        setSelectedInquiryId(row.original.id);
                        setShowSettledModal(true);
                      }}
                      title="Settle"
                    >
                      <FaMoneyBillTrendUp size={16} />
                    </span>
                  )}
                </div>
              );
            },
            meta: {
              headerClassName: "width-30",
              exportable: false,
            },
          },
        ]
      : []),
    {
      accessorKey: "lead_priority",
      header: "inquiry Priority",
      cell: (info) => {
        const val = info.getValue();
        const label =
          val === "1"
            ? "High"
            : val === "2"
            ? "Medium"
            : val === "3"
            ? "Low"
            : val;
        const color =
          val === "1"
            ? "warning"
            : val === "2"
            ? "primary"
            : val === "3"
            ? "success"
            : "secondary";

        return (
          <span className={`badge bg-soft-${color} text-${color}`}>
            {label}
          </span>
        );
      },
      defaultIsVisible: true,
    },
    {
      accessorKey: "LoanApplicationNumber",
      header: "Application Number",
      cell: (info) => info.getValue(),
      defaultIsVisible: true,
    },
    {
      accessorKey: "customer_name",
      header: "Customer Name",
      cell: (info) => info.getValue(),
      defaultIsVisible: true,
    },
    {
      accessorKey: "phone_number",
      header: "Phone Number",
      cell: (info) => info.getValue(),
      defaultIsVisible: true,
    },
    {
      accessorKey: "alternate_number",
      header: "Alternate Number",
      cell: (info) => info.getValue(),
    },
    {
      accessorKey: "email",
      header: "Email",
      cell: (info) => info.getValue(),
      defaultIsVisible: true,
    },
    {
      accessorKey: "address",
      header: "Address",
      cell: (info) => info.getValue(),
    },
    {
      accessorKey: "state_name",
      header: "State",
      cell: (info) => info.getValue(),
    },
    {
      accessorKey: "district_name",
      header: "district",
      cell: (info) => info.getValue(),
    },
    {
      accessorKey: "city_name",
      header: "City",
      cell: (info) => info.getValue(),
    },
    {
      accessorKey: "loan_type_name",
      header: "inquiry Type",
      cell: (info) => info.getValue(),
      defaultIsVisible: true,
    },
    {
      accessorKey: "loan_amount",
      header: "inquiry Amount",
      defaultIsVisible: true,
      cell: (info) =>
        Number(info.getValue()).toLocaleString("en-IN", {
          style: "currency",
          currency: "INR",
        }),
    },
    {
      accessorKey: "payouts",
      header: "inquiry Payouts",
      cell: (info) => info.getValue(),
      defaultIsVisible: true,
    },
    {
      accessorKey: "status_name",
      header: "inquiry Status",
      cell: (info) => {
        const status = info.getValue();
        return (
          <span
            style={{
              backgroundColor: "#e3f2fd", // light blue
              color: "#0d6efd", // blue text
              padding: "4px 8px",
              borderRadius: "0.25rem",
              fontSize: "11px",
              display: "inline-block",
            }}
          >
            {status}
          </span>
        );
      },
      defaultIsVisible: true,
    },
    {
      accessorKey: "inquiry_date",
      header: "Inquiry Date",
      cell: (info) => formatDateOnly(info.getValue()),
      defaultIsVisible: true,
    },
    {
      accessorKey: "refrence_name",
      header: "Reference Name",
      cell: (info) => info.getValue(),
      defaultIsVisible: true,
    },
    {
      accessorKey: "refrence_mobile",
      header: "Reference Mobile",
      cell: (info) => info.getValue(),
      defaultIsVisible: true,
    },
    {
      accessorKey: "follow_up_date",
      header: "Follow-up Date",
      cell: (info) => info.getValue(),
      defaultIsVisible: true,
    },
    {
      accessorKey: "lead_source_name",
      header: "Lead Source",
      cell: (info) => info.getValue(), // map from ID to name if needed
    },
    {
      accessorKey: "remarks",
      header: "Remarks",
      cell: (info) => info.getValue(),
    },
    {
      accessorKey: "assigned_user",
      header: "Assigned To",
      cell: (info) => info.getValue(), // You might want to map ID to name
    },
    {
      accessorKey: "is_converted",
      header: "Converted",
      cell: (info) => (info.getValue() === 1 ? "Yes" : "No"),
    },
    {
      accessorKey: "converted_date",
      header: "Converted Date",
      cell: (info) =>
        info.getValue()
          ? new Date(info.getValue()).toLocaleDateString("en-GB")
          : "—",
    },
    {
      accessorKey: "created_user",
      header: "Created By",
      cell: (info) => info.getValue(), // map if needed
    },
  ];

  useEffect(() => {
    const shouldFetch =
      !Array.isArray(filteredData) || filteredData.length === 0;

    if (shouldFetch) {
      fetchInitialData();
    } else {
      setBranchData(filteredData);
    }
  }, [filteredData]);

  return (
    <>
      <Table data={branchData} columns={columns} />

      {selectedRowIds.length > 0 && (
        <div className="mt-3 text-end d-flex justify-content-center md-10">
          <button className="btn btn-primary" onClick={handleSubmit}>
            Submit
          </button>
        </div>
      )}

      <SettledModal
        show={showSettledModal}
        onClose={() => setShowSettledModal(false)}
        onConfirm={async () => {
          if (!payoutAmount || !payoutDate || !payoutType || !remark) {
            topTost({
              title: "Converted updated successfully",
              icon: "success",
            });
            return;
          }
          setLoading(true);
          setSuccessMessage("");
          setErrorMessage("");

          const formData = new FormData();
          formData.append("userId", userId);
          formData.append("moduleId", M_id);

          selectedRowIds
            .filter((id) => id != null)
            .forEach((id) => {
              formData.append("id[]", id);
            });

          formData.append("IsPaid", 1);
          try {
            const response = await fetchFormData(
              "update-loan-payout-ById",
              formData,
              "POST"
            );

            const result = await response.json();

            if (response.code === 200) {
              topTost({
                title: response.message,
                icon: "success",
              });

              // Refresh table or data
              fetchInitialData();
              setSelectedRowIds([]);
              setShowSettledModal(false);

              // Reset form values
              setPayoutAmount("");
              setPayoutDate("");
              setPayoutType("");
              setremark("");
              setTransactionDetails("");
              setIsSettled(false);
            } else {
              throw new Error(result?.message || "Something went wrong");
            }
          } catch (error) {
            setErrorMessage(error.message);
            topTost({ title: error.message, icon: "error" });
          } finally {
            setLoading(false);
          }
        }}
        isSettled={isSettled}
        setIsSettled={setIsSettled}
        transactionDetails={transactionDetails}
        setTransactionDetails={setTransactionDetails}
        selectedInquiryId={selectedInquiryId}
        payoutDate={payoutDate}
        setPayoutDate={setPayoutDate}
        payoutType={payoutType}
        setPayoutType={setPayoutType}
        remark={remark}
        setremark={setremark}
      />
    </>
  );
};

export default OpenLoanLeadsTable;
